import React, { useState, useEffect, useRef } from 'react';
import { Camera, Layout, Share2, RefreshCw, Heart, Edit3, Image as ImageIcon, Users, Plus, Trash2, ChevronRight, Star, Check, Monitor, PenTool, MessageSquare, X, Send } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { getFirestore, collection, addDoc, query, onSnapshot, doc, updateDoc, deleteDoc, increment, serverTimestamp, arrayUnion, arrayRemove } from 'firebase/firestore';

// --- Helper for Safe Environment Variable Access ---
const getEnvVar = (key: string) => {
  try {
    if (typeof process !== 'undefined' && process && process.env) {
      return process.env[key];
    }
  } catch (e) {
    return undefined;
  }
  return undefined;
};

// --- Firebase Configuration ---
const manualConfig = {
  apiKey: "AIzaSyCNRF8OlmRDSOA0ILblDULse7W2udPCFR4", 
  authDomain: "blueprint-e1c5e.firebaseapp.com",
  projectId: "blueprint-e1c5e",
  storageBucket: "blueprint-e1c5e.firebasestorage.app",
  messagingSenderId: "1009234056890",
  appId: "1:1009234056890:web:b8172a03fb2774b6d19483"
};

// --- API Key Configuration ---
// 제공해주신 API 키를 여기에 직접 적용했습니다.
const apiKey = "AIzaSyBFNBhjGJE8vAWz0XfOP4tzOSq6z02y_Fc"; 

// -----------------------------------------------------------
const getGlobal = (key: string) => {
  if (typeof window !== 'undefined') {
    return (window as any)[key];
  }
  return undefined;
};

const globalFirebaseConfig = getGlobal('__firebase_config');
const firebaseConfig = globalFirebaseConfig ? JSON.parse(globalFirebaseConfig) : manualConfig;

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

const globalAppId = getGlobal('__app_id');
const appId = globalAppId || 'oh-boram-class-gallery-v1'; 

// --- 이미지 압축 유틸리티 ---
const compressImage = (base64Str: string, maxWidth = 800, quality = 0.6): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; 
    img.src = base64Str;

    img.onload = () => {
      let currentWidth = maxWidth;
      let attemptCount = 0;
        
      const attemptCompression = (width: number, q: number) => {
        attemptCount++;
        const canvas = document.createElement('canvas');
        let h = (width / img.width) * img.height;
        
        canvas.width = Math.floor(width);
        canvas.height = Math.floor(h);
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
            reject(new Error("Canvas context error"));
            return;
        }

        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, width, h);
        
        const result = canvas.toDataURL('image/jpeg', q);
        
        if (result.length > 700000 && width > 300 && attemptCount < 5) {
          attemptCompression(width * 0.8, q * 0.9);
        } else {
          resolve(result);
        }
      };
      
      attemptCompression(img.width > currentWidth ? currentWidth : img.width, quality);
    };

    img.onerror = (err) => {
        console.error("Image compression error:", err);
        reject(new Error("이미지 처리 실패"));
    };
  });
};

// --- UI Components ---
const SectionContainer = ({ children, title, icon: Icon, color }: any) => (
  <div className={`bg-white p-6 md:p-8 rounded-[2rem] shadow-sm border border-slate-100 relative overflow-hidden group hover:shadow-lg transition-all duration-300`}>
    <div className={`absolute top-0 left-0 w-1.5 h-full bg-${color}-500 opacity-0 group-hover:opacity-100 transition-opacity`}></div>
    <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-100">
      <div className={`bg-${color}-50 p-3 rounded-2xl text-${color}-600 ring-4 ring-${color}-50/50`}>
        <Icon size={24} strokeWidth={2.5} />
      </div>
      <h3 className="font-['Noto_Sans_KR'] font-bold text-xl text-slate-800">{title}</h3>
    </div>
    <div className="space-y-8">
      {children}
    </div>
  </div>
);

const InputLabel = ({ label, required }: any) => (
  <label className="block text-lg font-['Noto_Sans_KR'] font-bold text-slate-700 mb-3 tracking-tight flex items-center gap-1">
    {label} {required && <span className="text-pink-500 text-xl leading-none">•</span>}
  </label>
);

// --- Main App ---
export default function App() {
  const [user, setUser] = useState<any>(null);
  const [view, setView] = useState('create'); 
  const [authError, setAuthError] = useState<string | null>(null);
  const [stylesLoaded, setStylesLoaded] = useState(false); 
    
  useEffect(() => {
    // 1. 브라우저 탭 제목 설정
    document.title = "나의 미래수업 청사진";

    // 2. 메타 태그(카카오톡 공유 제목 등) 동적 설정
    const setMetaTag = (property: string, content: string) => {
        let element = document.querySelector(`meta[property="${property}"]`);
        if (!element) {
            element = document.createElement('meta');
            element.setAttribute('property', property);
            document.head.appendChild(element);
        }
        element.setAttribute('content', content);
    };

    setMetaTag('og:title', '나의 미래수업 청사진');
    setMetaTag('og:description', 'AI·디지털 기반 수업 업그레이드 - 선생님만의 교실을 설계해보세요.');

    if (!document.getElementById('tailwind-cdn')) {
      const script = document.createElement('script');
      script.id = 'tailwind-cdn';
      script.src = "https://cdn.tailwindcss.com";
      script.onload = () => setStylesLoaded(true); 
      document.head.appendChild(script);
    } else {
      setStylesLoaded(true);
    }

    if (!document.getElementById('google-font')) {
      const link = document.createElement('link');
      link.id = 'google-font';
      link.href = "https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;700;900&display=swap";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }

    if (!document.getElementById('global-font-style')) {
      const style = document.createElement('style');
      style.id = 'global-font-style';
      style.innerHTML = `body { font-family: 'Noto Sans KR', sans-serif !important; }`;
      document.head.appendChild(style);
    }

    const initAuth = async () => {
      try {
        const initialToken = getGlobal('__initial_auth_token');
        if (initialToken) {
          await signInWithCustomToken(auth, initialToken);
        } else {
          await signInAnonymously(auth);
        }
      } catch (error: any) {
        console.error("Auth error:", error);
        setAuthError(error.message);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  if (!stylesLoaded) {
    return (
      <div className="flex flex-col h-screen justify-center items-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-slate-500 font-medium">디자인을 불러오고 있습니다...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 font-['Noto_Sans_KR'] selection:bg-indigo-100 pb-20">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50 shadow-sm transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4 cursor-pointer group" onClick={() => setView('create')}>
            <div className="bg-gradient-to-br from-indigo-600 to-violet-600 p-2 md:p-3 rounded-xl md:rounded-2xl text-white shadow-lg shadow-indigo-200 group-hover:scale-105 transition-transform duration-300">
              <Star size={20} className="md:w-6 md:h-6" fill="white" />
            </div>
            <div>
              <h1 className="font-black text-xl md:text-2xl leading-none tracking-tight text-slate-900 mb-1 font-['Noto_Sans_KR']">나의 미래수업 청사진</h1>
              <p className="hidden md:block text-sm text-slate-500 font-medium font-['Noto_Sans_KR']">AI·디지털 기반 수업 업그레이드</p>
            </div>
          </div>
            
          <nav className="flex gap-2 bg-slate-100/50 p-1 rounded-full border border-slate-200">
            <button 
              onClick={() => setView('create')}
              className={`px-4 md:px-6 py-2 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2 ${view === 'create' ? 'bg-white text-indigo-600 shadow-sm ring-1 ring-black/5' : 'text-slate-500 hover:bg-white/60 hover:text-slate-700'}`}
            >
              <Edit3 size={16} />
              <span className="hidden md:inline font-['Noto_Sans_KR']">설계하기</span>
            </button>
            <button 
              onClick={() => setView('gallery')}
              className={`px-4 md:px-6 py-2 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2 ${view === 'gallery' ? 'bg-white text-pink-600 shadow-sm ring-1 ring-black/5' : 'text-slate-500 hover:bg-white/60 hover:text-slate-700'}`}
            >
              <Users size={16} />
              <span className="hidden md:inline font-['Noto_Sans_KR']">갤러리 워크</span>
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        {!user ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-indigo-100 rounded-full animate-ping opacity-20"></div>
              <div className="relative bg-white p-6 rounded-full shadow-2xl border border-indigo-50">
                <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-slate-100 border-t-indigo-600"></div>
              </div>
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2 font-['Noto_Sans_KR']">선생님을 위한 공간을 준비하고 있어요</h3>
            <p className="text-slate-500 font-['Noto_Sans_KR']">잠시만 기다려주세요...</p>
            {authError && (
              <div className="mt-8 p-6 bg-red-50 text-red-600 rounded-2xl border border-red-100 text-sm max-w-md text-center shadow-sm">
                <p className="font-bold text-lg mb-1">연결에 실패했습니다</p>
                <p className="opacity-90">{authError}</p>
              </div>
            )}
          </div>
        ) : (
          <>
            {view === 'create' && <CreateBlueprint user={user} onViewGallery={() => setView('gallery')} />}
            {view === 'gallery' && <Gallery user={user} />}
          </>
        )}
      </main>
        
      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-6 pt-12 text-center border-t border-slate-200/60 mt-12">
        <p className="text-slate-400 text-sm font-['Noto_Sans_KR']">© 서울영화초 오보람. All rights reserved.</p>
        <p className="text-slate-300 text-xs mt-2 font-['Noto_Sans_KR']">Gallery ID: {appId}</p>
      </footer>
    </div>
  );
}

// --- Blueprint Creation Component ---
function CreateBlueprint({ user, onViewGallery }: any) {
  const [formData, setFormData] = useState<any>({
    author: '', 
    grade: '4학년',
    semester: '1학기',
    subject: '사회',
    topic: '우리 지역의 문화유산 답사하기', 
    standards: ['우리 지역의 문화유산을 조사하고 소중히 여기는 태도를 갖는다.'], 
    coreIdea: '문화유산의 가치 재발견', 
    lessonIdea: '가상현실(VR)과 태블릿을 활용해 생생하게 체험하고 홍보 자료 만들기', 
    tech: 'VR기기, 태블릿PC, 전자칠판', 
    activities: ['VR로 우리 지역 문화유산 실감나게 체험하기', '태블릿PC로 조사한 내용을 바탕으로 홍보 포스터 만들기', '전자칠판을 활용하여 모둠별 결과물 발표하기'], 
    teacherAgency: '학생들의 흥미를 고려하여 다양한 디지털 매체를 선정하고 제공함', 
    studentSupport: '디지털 기기 조작이 미숙한 학생을 위해 튜토리얼 영상 제공 및 이질 집단 모둠 구성',
    style: '3d-isometric'
  });

  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [modificationPrompt, setModificationPrompt] = useState('');

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (field: string, index: number, value: string) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData((prev: any) => ({ ...prev, [field]: newArray }));
  };

  const addArrayItem = (field: string) => {
    setFormData((prev: any) => ({ ...prev, [field]: [...formData[field], ''] }));
  };

  const removeArrayItem = (field: string, index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: formData[field].filter((_: any, i: number) => i !== index)
    }));
  };

  const handleGenerate = async (isMod = false) => {
    if (!formData.subject || !formData.tech) {
      alert("교과목과 활용 에듀테크는 필수 입력 사항입니다.");
      return;
    }
    
    // API KEY Logic: Use the hardcoded key
    const currentApiKey = apiKey;

    if (!currentApiKey) {
      alert("AI 이미지 생성을 위한 API 키가 설정되지 않았습니다.");
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);
      
    try {
      let prompt = `A high-quality, colorful 3D isometric blueprint illustration of a future classroom in South Korea. 
      Subject: ${formData.subject}. Grade: ${formData.grade}. Topic: ${formData.topic}.
      Key Elements: ${formData.tech} (Make visible). Activities: ${formData.activities.join(', ')}.
      Atmosphere: Bright, collaborative, modern. Style: 3D isometric view, cute professional, like a diorama, vibrant colors. No text.`;

      if (isMod && modificationPrompt) {
        prompt += `\n\nModification Request: ${modificationPrompt}. Adjust accordingly.`;
      }

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-image-preview:generateContent?key=${currentApiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: { responseModalities: ["IMAGE"] }
          }),
        }
      );

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error?.message || 'Image generation failed');
      }

      const data = await response.json();
      const base64Image = data.candidates?.[0]?.content?.parts?.find((p: any) => p.inlineData)?.inlineData?.data;

      if (!base64Image) {
        throw new Error('No image data received');
      }

      setGeneratedImage(`data:image/png;base64,${base64Image}`);
      if (isMod) setModificationPrompt(''); 

    } catch (error: any) {
      console.error("Generation Error:", error);
      alert(`이미지 생성 실패:\n${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveToGallery = async () => {
    if (!generatedImage) return;
    if (!confirm("갤러리에 게시하시겠습니까?")) return;

    try {
      const compressedImage = await compressImage(generatedImage, 800, 0.6);

      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'blueprints'), {
        userId: user.uid,
        userName: formData.author || '익명 선생님', 
        ...formData,
        imageUrl: compressedImage, 
        likes: 0,
        likedBy: [], 
        commentCount: 0,
        createdAt: serverTimestamp(),
      });
      alert("갤러리에 등록되었습니다!");
      onViewGallery();
    } catch (error: any) {
      console.error("Save error:", error);
      alert(`저장에 실패했습니다.\n${error.message}`);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
      {/* Form Section */}
      <div className="lg:col-span-7 space-y-8">
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-8 md:p-10 rounded-[2.5rem] shadow-xl text-white relative overflow-hidden ring-1 ring-white/20">
          <div className="relative z-10">
            <h2 className="text-3xl font-black mb-4 flex items-center gap-3 font-['Noto_Sans_KR']"><Star size={32} className="text-yellow-300 fill-yellow-300"/>수업 설계하기</h2>
            <p className="text-indigo-100 text-lg font-medium font-['Noto_Sans_KR']">선생님의 상상력을 입력해주세요. AI가 <span className="text-white font-bold border-b-2 border-yellow-400">생생한 3D 청사진</span>으로 그려드립니다.</p>
          </div>
        </div>

        <SectionContainer title="기본 정보" icon={Users} color="blue">
          <div><InputLabel label="작성자 선생님" /><input type="text" name="author" value={formData.author} onChange={handleInputChange} placeholder="선생님의 성함을 입력해주세요" className="w-full border-2 border-slate-100 rounded-2xl p-5 text-lg focus:ring-4 focus:ring-blue-100 outline-none bg-slate-50/50 hover:bg-white focus:bg-white transition-all font-['Noto_Sans_KR']"/></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <div className="col-span-1"><InputLabel label="학년" /><select name="grade" value={formData.grade} onChange={handleInputChange} className="w-full border-2 border-slate-100 rounded-2xl p-5 text-lg focus:ring-4 focus:ring-blue-100 outline-none bg-slate-50/50 cursor-pointer hover:bg-white font-['Noto_Sans_KR']">{[...Array(6)].map((_, i) => <option key={i} value={`${i+1}학년`}>{i+1}학년</option>)}</select></div>
            <div className="col-span-1"><InputLabel label="학기" /><select name="semester" value={formData.semester} onChange={handleInputChange} className="w-full border-2 border-slate-100 rounded-2xl p-5 text-lg focus:ring-4 focus:ring-blue-100 outline-none bg-slate-50/50 cursor-pointer hover:bg-white font-['Noto_Sans_KR']"><option value="1학기">1학기</option><option value="2학기">2학기</option></select></div>
            <div className="col-span-2"><InputLabel label="교과목" /><input type="text" name="subject" value={formData.subject} onChange={handleInputChange} placeholder="예: 사회" className="w-full border-2 border-slate-100 rounded-2xl p-5 text-lg focus:ring-4 focus:ring-blue-100 outline-none bg-slate-50/50 hover:bg-white focus:bg-white transition-all font-['Noto_Sans_KR']"/></div>
          </div>
          <div><InputLabel label="단원명 (또는 주제)" /><input type="text" name="topic" value={formData.topic} onChange={handleInputChange} placeholder="예: 우리 지역의 문화유산 답사하기" className="w-full border-2 border-slate-100 rounded-2xl p-5 text-lg focus:ring-4 focus:ring-blue-100 outline-none bg-slate-50/50 hover:bg-white focus:bg-white transition-all font-['Noto_Sans_KR']"/></div>
        </SectionContainer>

        <SectionContainer title="수업 상세 설계" icon={Edit3} color="indigo">
          <div className="bg-indigo-50/50 p-6 rounded-3xl border border-indigo-100/50">
            <div className="flex justify-between items-center mb-4"><InputLabel label="성취기준" /><button onClick={() => addArrayItem('standards')} className="bg-white text-indigo-600 px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-bold shadow-sm font-['Noto_Sans_KR']"><Plus size={18}/>추가하기</button></div>
            <div className="space-y-4">{formData.standards.map((std: string, index: number) => (<div key={index} className="flex gap-3 items-start"><span className="bg-white text-indigo-500 font-black text-sm py-5 px-3 rounded-2xl border border-indigo-100 min-w-[44px] text-center shadow-sm">{index + 1}</span><textarea value={std} rows={2} onChange={(e) => handleArrayChange('standards', index, e.target.value)} placeholder="성취기준 입력" className="flex-1 border-2 border-indigo-100 rounded-2xl p-4 text-lg focus:ring-4 focus:ring-indigo-100 outline-none resize-none bg-white font-['Noto_Sans_KR']"/>{formData.standards.length > 1 && <button onClick={() => removeArrayItem('standards', index)} className="text-slate-300 hover:text-red-500 p-3 self-center"><Trash2 size={20}/></button>}</div>))}</div>
          </div>
          <div className="grid grid-cols-1 gap-6">
            <div><InputLabel label="핵심 아이디어" /><textarea name="coreIdea" value={formData.coreIdea} onChange={handleInputChange} rows={3} className="w-full border-2 border-slate-100 rounded-2xl p-5 text-lg focus:ring-4 focus:ring-indigo-100 outline-none resize-none bg-slate-50/50 hover:bg-white focus:bg-white font-['Noto_Sans_KR']"/></div>
            <div><InputLabel label="수업 아이디어" /><textarea name="lessonIdea" value={formData.lessonIdea} onChange={handleInputChange} rows={3} className="w-full border-2 border-slate-100 rounded-2xl p-5 text-lg focus:ring-4 focus:ring-indigo-100 outline-none resize-none bg-slate-50/50 hover:bg-white focus:bg-white font-['Noto_Sans_KR']"/></div>
          </div>
          <div><InputLabel label="활용 에듀테크 (필수)" required /><div className="relative"><div className="absolute top-5 left-5 text-indigo-500 bg-indigo-50 p-2 rounded-xl"><Monitor size={24} /></div><input type="text" name="tech" value={formData.tech} onChange={handleInputChange} placeholder="예: VR기기, 태블릿PC, 챗봇" className="w-full border-2 border-indigo-100 rounded-2xl p-5 pl-20 text-lg font-bold text-indigo-900 focus:ring-4 focus:ring-indigo-100 outline-none bg-indigo-50/30 hover:bg-white focus:bg-white transition-all font-['Noto_Sans_KR']"/></div></div>
        </SectionContainer>

        <SectionContainer title="활동 흐름 및 주도성" icon={PenTool} color="violet">
          <div className="bg-violet-50/50 p-6 rounded-3xl border border-violet-100/50">
            <div className="flex justify-between items-center mb-4"><InputLabel label="활동 순서 (Flow)" /><button onClick={() => addArrayItem('activities')} className="bg-white text-violet-600 px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-bold shadow-sm font-['Noto_Sans_KR']"><Plus size={18}/>단계 추가</button></div>
            <div className="space-y-4">{formData.activities.map((act: string, index: number) => (<div key={index} className="flex gap-4 items-center"><div className="flex flex-col items-center justify-center min-w-[70px] h-[70px] bg-white rounded-2xl border-2 border-violet-100 shadow-sm"><span className="text-[10px] text-slate-400 font-bold uppercase">Step</span><span className="text-2xl font-black text-violet-600">{index + 1}</span></div><input type="text" value={act} onChange={(e) => handleArrayChange('activities', index, e.target.value)} placeholder="활동 내용 입력" className="flex-1 border-2 border-violet-100 rounded-2xl p-5 text-lg font-medium focus:ring-4 focus:ring-violet-100 outline-none bg-white font-['Noto_Sans_KR']"/>{formData.activities.length > 1 && <button onClick={() => removeArrayItem('activities', index)} className="text-slate-300 hover:text-red-500 p-3"><Trash2 size={22}/></button>}</div>))}</div>
          </div>
          <div className="space-y-6 pt-4">
            <div><InputLabel label="교사 주도성 발휘 Point" /><textarea name="teacherAgency" value={formData.teacherAgency} onChange={handleInputChange} rows={3} className="w-full border-2 border-slate-100 rounded-2xl p-5 text-lg focus:ring-4 focus:ring-violet-100 outline-none resize-none bg-slate-50/50 hover:bg-white focus:bg-white font-['Noto_Sans_KR']"/></div>
            <div><InputLabel label="학생 맞춤형 성장 지원 Point" /><textarea name="studentSupport" value={formData.studentSupport} onChange={handleInputChange} rows={3} className="w-full border-2 border-slate-100 rounded-2xl p-5 text-lg focus:ring-4 focus:ring-violet-100 outline-none resize-none bg-slate-50/50 hover:bg-white focus:bg-white font-['Noto_Sans_KR']"/></div>
          </div>
        </SectionContainer>

        <div className="sticky bottom-6 z-10 pt-4">
          <button onClick={() => handleGenerate(false)} disabled={isGenerating} className="w-full bg-slate-900 text-white py-5 rounded-[2rem] font-bold text-xl hover:bg-slate-800 hover:scale-[1.01] transition-all flex items-center justify-center gap-4 shadow-xl border-4 border-white/50 ring-4 ring-slate-100 disabled:opacity-80 font-['Noto_Sans_KR']">
            {isGenerating ? <><RefreshCw className="animate-spin" size={28}/>설계 중...</> : <><Camera size={28}/>청사진 생성하기</>}
          </button>
        </div>
      </div>

      {/* Preview Section */}
      <div className="lg:col-span-5 space-y-8 sticky top-28">
        <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-slate-100 relative overflow-hidden">
          <h2 className="text-2xl font-extrabold mb-6 flex items-center gap-3 text-slate-800 font-['Noto_Sans_KR']"><span className="bg-indigo-50 text-indigo-600 p-3 rounded-2xl"><Layout size={24}/></span>청사진 미리보기</h2>
          <div className="aspect-[4/3] bg-slate-50 rounded-3xl overflow-hidden flex items-center justify-center relative group border-2 border-slate-100 shadow-inner">
            {generatedImage ? <img src={generatedImage} alt="Blueprint" className="w-full h-full object-contain p-2"/> : <div className="text-center text-slate-400 p-10">{isGenerating ? <div className="space-y-6"><div className="w-24 h-24 border-[6px] border-indigo-600 rounded-full border-t-transparent animate-spin mx-auto"/></div> : <p className="font-bold text-lg font-['Noto_Sans_KR']">왼쪽 내용을 입력하고<br/>생성 버튼을 눌러주세요</p>}</div>}
          </div>
          {generatedImage && (
            <div className="mt-8 space-y-4">
              <div className="bg-slate-50 p-5 rounded-3xl border border-slate-100">
                <label className="text-sm font-extrabold text-slate-600 mb-3 block font-['Noto_Sans_KR']">수정 요청하기 (AI와 대화)</label>
                <div className="flex gap-2"><input type="text" value={modificationPrompt} onChange={(e) => setModificationPrompt(e.target.value)} placeholder="예) 책상을 모둠형으로 바꿔줘" className="flex-1 border-2 border-slate-200 rounded-2xl p-4 outline-none font-['Noto_Sans_KR']"/><button onClick={() => handleGenerate(true)} disabled={isGenerating} className="bg-slate-800 text-white px-6 rounded-2xl font-bold font-['Noto_Sans_KR']">수정</button></div>
              </div>
              <button 
                onClick={handleSaveToGallery} 
                className="w-full bg-gradient-to-r from-indigo-500 to-violet-600 text-white py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 shadow-lg font-['Noto_Sans_KR']"
              >
                <Share2 size={22}/>
                갤러리에 게시하기
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Detail Modal Component ---
function BlueprintDetailModal({ blueprint, onClose, user }: any) {
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!blueprint) return;
    
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'blueprints', blueprint.id, 'comments')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedComments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      fetchedComments.sort((a: any, b: any) => {
        const timeA = a.createdAt?.seconds || Date.now()/1000;
        const timeB = b.createdAt?.seconds || Date.now()/1000;
        return timeA - timeB;
      });
      setComments(fetchedComments);
    });
    return () => unsubscribe();
  }, [blueprint]);

  const handleAddComment = async (e: any) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'blueprints', blueprint.id, 'comments'), {
        text: newComment,
        userId: user.uid,
        userName: '선생님', 
        createdAt: serverTimestamp()
      });
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'blueprints', blueprint.id), {
        commentCount: increment(1)
      });
      setNewComment("");
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (error) {
      console.error("Comment failed:", error);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
      <div className="bg-white w-full max-w-7xl h-[90vh] rounded-[2.5rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300 flex flex-col relative">
        
        {/* 닫기 버튼 - 우측 상단 고정 */}
        <button onClick={onClose} className="absolute top-6 right-6 p-3 bg-white/80 hover:bg-slate-100 rounded-full transition-colors z-50 shadow-sm border border-slate-200">
          <X size={24}/>
        </button>

        {/* 메인 콘텐츠 영역: 좌측 이미지 / 우측 정보 (스크롤) */}
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
            
            {/* Left: 이미지 영역 (화면의 55%) */}
            <div className="lg:w-[55%] h-[40vh] lg:h-full bg-[#EAEFF5] flex items-center justify-center p-6 border-b lg:border-b-0 lg:border-r border-slate-200 relative">
                {blueprint.imageUrl ? (
                    <img 
                        src={blueprint.imageUrl} 
                        alt={blueprint.topic} 
                        className="max-w-full max-h-full object-contain drop-shadow-xl" 
                    />
                ) : (
                    <div className="text-slate-400 flex flex-col items-center">
                        <ImageIcon size={64} className="mb-4 opacity-30"/>
                        <p>이미지가 없습니다</p>
                    </div>
                )}
            </div>

            {/* Right: 정보 및 댓글 목록 영역 (화면의 45%) - 여기가 스크롤 됩니다 */}
            <div className="lg:w-[45%] h-full overflow-y-auto bg-white custom-scrollbar">
                <div className="p-8 pb-32"> {/* 하단 여백 확보 */}
                    {/* Header Info */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        <span className="bg-indigo-600 text-white px-3 py-1 rounded-full text-xs font-bold">{blueprint.grade}</span>
                        <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-['Noto_Sans_KR'] font-bold">{blueprint.subject}</span>
                    </div>
                    <h2 className="text-3xl font-extrabold text-slate-900 mb-2 font-['Noto_Sans_KR'] leading-tight">{blueprint.topic}</h2>
                    <p className="text-slate-600 mb-8 font-medium font-['Noto_Sans_KR'] text-lg">By. {blueprint.userName || '익명'}</p>

                    {/* Detailed Sections */}
                    <div className="space-y-8">
                         <div>
                            <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2 font-['Noto_Sans_KR'] text-lg"><Check size={20} className="text-indigo-500"/>활동 흐름</h4>
                            <div className="flex flex-col gap-2">
                                {blueprint.activities?.map((act: string, i: number) => (
                                    // [수정] Activity Flow의 폰트 및 패딩을 Lesson Idea와 통일 (p-4, text-base)
                                    <div key={i} className="flex gap-3 items-start bg-slate-50 p-4 rounded-xl border border-slate-100">
                                        <span className="bg-white text-indigo-600 font-bold text-xs px-2 py-1 rounded border border-indigo-100 mt-1">Step {i+1}</span>
                                        <span className="text-slate-700 text-base font-['Noto_Sans_KR'] leading-relaxed">{act}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                         <div>
                            <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2 font-['Noto_Sans_KR'] text-lg"><Monitor size={20} className="text-indigo-500"/>활용 에듀테크</h4>
                            <div className="flex flex-wrap gap-2">
                              {blueprint.tech?.split(',').map((t: string, i: number) => <span key={i} className="bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg text-sm font-bold font-['Noto_Sans_KR']">#{t.trim()}</span>)}
                            </div>
                        </div>

                        {blueprint.lessonIdea && (
                            <div>
                                <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2 font-['Noto_Sans_KR'] text-lg"><Star size={20} className="text-yellow-500"/>수업 아이디어</h4>
                                <p className="text-slate-700 text-base bg-yellow-50/50 p-4 rounded-xl border border-yellow-100/50 leading-relaxed">{blueprint.lessonIdea}</p>
                            </div>
                        )}
                        
                        {(blueprint.teacherAgency || blueprint.studentSupport) && (
                            <div className="grid grid-cols-1 gap-4">
                                {blueprint.teacherAgency && (
                                    <div>
                                        {/* [수정] 제목 크기, 여백, 아이콘 크기를 다른 영역과 통일 (text-lg, mb-3, size={20}) */}
                                        <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2 font-['Noto_Sans_KR'] text-lg"><PenTool size={20} className="text-violet-500"/>교사 주도성 Point</h4>
                                        <p className="text-slate-700 text-base bg-violet-50/50 p-4 rounded-xl border border-violet-100/50 leading-relaxed">{blueprint.teacherAgency}</p>
                                    </div>
                                )}
                                {blueprint.studentSupport && (
                                    <div>
                                        {/* [수정] 제목 크기, 여백, 아이콘 크기를 다른 영역과 통일 (text-lg, mb-3, size={20}) */}
                                        <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2 font-['Noto_Sans_KR'] text-lg"><Heart size={20} className="text-pink-500"/>학생 맞춤형 지원 Point</h4>
                                        <p className="text-slate-700 text-base bg-pink-50/50 p-4 rounded-xl border border-pink-100/50 leading-relaxed">{blueprint.studentSupport}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Comments List Section */}
                    <div className="mt-12 pt-10 border-t border-slate-100">
                         <h3 className="font-bold text-xl flex items-center gap-2 text-slate-800 font-['Noto_Sans_KR'] mb-6"><MessageSquare size={24} className="text-pink-500"/>동료 피드백</h3>
                         <div className="space-y-4">
                            {comments.length === 0 ? (
                                <div className="text-center py-10 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                    <MessageSquare size={32} className="mx-auto text-slate-300 mb-2"/>
                                    <p className="text-slate-400">아직 피드백이 없습니다.<br/>첫 번째 의견을 남겨보세요!</p>
                                </div>
                            ) : (
                                comments.map((comment) => (
                                    <div key={comment.id} className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="font-bold text-slate-900 font-['Noto_Sans_KR']">{comment.userName || '선생님'}</span>
                                            <span className="text-xs text-slate-400">방금 전</span>
                                        </div>
                                        <p className="text-slate-700 text-sm leading-relaxed font-['Noto_Sans_KR']">{comment.text}</p>
                                    </div>
                                ))
                            )}
                         </div>
                    </div>
                    {/* 스크롤 포커싱용 빈 태그 */}
                    <div ref={messagesEndRef} /> 
                </div>
            </div>
        </div>

        {/* Footer: 댓글 입력창 (최하단 고정) */}
        <div className="flex-none bg-white p-4 border-t border-slate-200 lg:px-8 lg:py-5 z-10 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
            <form onSubmit={handleAddComment} className="flex gap-3 max-w-5xl mx-auto w-full">
                <div className="relative flex-1">
                     <input 
                        type="text" 
                        value={newComment} 
                        onChange={(e) => setNewComment(e.target.value)} 
                        placeholder="선생님의 수업에 대해 따뜻한 피드백과 질문을 남겨주세요..." 
                        className="w-full bg-slate-100 border-2 border-slate-200 rounded-full py-4 pl-6 pr-4 text-base focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all font-['Noto_Sans_KR']"
                    />
                </div>
                <button 
                    type="submit" 
                    disabled={!newComment.trim()} 
                    className="bg-indigo-600 text-white rounded-full px-8 py-4 font-bold hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-indigo-200 whitespace-nowrap"
                >
                    <Send size={20}/>
                    <span className="hidden md:inline">등록</span>
                </button>
            </form>
        </div>

      </div>
    </div>
  );
}

// --- Gallery Component ---
function Gallery({ user }: any) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'blueprints'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedItems = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      fetchedItems.sort((a: any, b: any) => {
        const timeA = a.createdAt?.seconds || Date.now()/1000;
        const timeB = b.createdAt?.seconds || Date.now()/1000;
        return timeB - timeA;
      });

      setItems(fetchedItems);
      setLoading(false);
      setErrorMsg(null);
    }, (error: any) => {
        console.error("Error fetching gallery:", error);
        setLoading(false);
        if (error.code === 'permission-denied') {
            setErrorMsg("데이터를 불러올 권한이 없습니다. (Firebase Console 규칙을 확인하세요)");
        } else if (error.code === 'resource-exhausted') {
            setErrorMsg("데이터 사용량이 초과되었습니다.");
        } else {
            setErrorMsg(`갤러리를 불러오지 못했습니다. (${error.message})`);
        }
    });
    return () => unsubscribe();
  }, [user]);

  // [수정] 좋아요 토글 로직 강화 (Vercel 배포 시 오류 디버깅용 alert 추가)
  const handleLike = async (e: any, item: any) => {
    e.stopPropagation(); // 카드 클릭 이벤트 전파 방지
    if (!user) {
        alert("좋아요를 누르려면 로그인이 필요합니다.");
        return;
    }

    const itemRef = doc(db, 'artifacts', appId, 'public', 'data', 'blueprints', item.id);
    
    // 안전하게 배열 확인
    const currentLikedBy = item.likedBy || [];
    const isLiked = currentLikedBy.includes(user.uid);

    try {
      if (isLiked) {
        // 이미 좋아요를 눌렀다면 -> 취소 (감소 및 배열에서 제거)
        await updateDoc(itemRef, { 
          likes: increment(-1),
          likedBy: arrayRemove(user.uid)
        });
      } else {
        // 좋아요가 없다면 -> 추가 (증가 및 배열에 추가)
        await updateDoc(itemRef, { 
          likes: increment(1),
          likedBy: arrayUnion(user.uid)
        });
      }
    } catch (error: any) {
      console.error("Like error:", error);
      // Vercel 등 배포 환경에서 권한 문제나 네트워크 문제인지 파악하기 위해 알림 추가
      alert(`좋아요 반영에 실패했습니다.\n오류: ${error.message}\n(Firebase 규칙 권한이나 네트워크를 확인해주세요)`);
    }
  };

  const handleDelete = async (e: any, itemId: string) => {
    e.stopPropagation();
    if (!confirm("정말로 이 청사진을 삭제하시겠습니까?")) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'blueprints', itemId));
      alert("삭제되었습니다.");
    } catch (error) {
      console.error("Delete failed", error);
      alert("삭제 권한이 없거나 오류가 발생했습니다.");
    }
  };

  if (loading) return <div className="text-center py-40"><div className="animate-spin rounded-full h-16 w-16 border-[6px] border-slate-100 border-t-indigo-600 mx-auto mb-6"></div><p className="text-xl text-slate-600 font-bold font-['Noto_Sans_KR']">갤러리를 불러오는 중입니다...</p></div>;

  if (errorMsg) return (
    <div className="text-center py-40 bg-red-50 rounded-[3rem] border-4 border-dashed border-red-200 mx-4">
        <p className="text-red-500 text-xl font-bold font-['Noto_Sans_KR'] mb-2">오류가 발생했습니다</p>
        <p className="text-slate-600">{errorMsg}</p>
        <button onClick={() => window.location.reload()} className="mt-6 bg-red-100 text-red-600 px-6 py-2 rounded-full font-bold hover:bg-red-200 transition-colors">새로고침</button>
    </div>
  );

  return (
    <div>
      <div className="mb-16 text-center max-w-3xl mx-auto">
        <span className="bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-extrabold tracking-wide mb-4 inline-block font-['Noto_Sans_KR']">INSIGHT GALLERY</span>
        <h2 className="text-4xl font-extrabold text-slate-900 mb-4 tracking-tight font-['Noto_Sans_KR']">미래수업 청사진 갤러리</h2>
        <p className="text-slate-500 text-lg font-['Noto_Sans_KR']">동료 선생님들이 설계한 교실을 <span className="text-indigo-600 font-bold">클릭</span>하여 자세히 보고 피드백을 나눠보세요.</p>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-40 bg-white rounded-[3rem] border-4 border-dashed border-slate-100"><p className="text-slate-400 text-xl font-bold font-['Noto_Sans_KR']">아직 등록된 청사진이 없습니다.<br/>첫 번째 주인공이 되어보세요!</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 px-4">
          {items.map((item) => {
            // 좋아요 여부 확인
            const isLiked = item.likedBy?.includes(user.uid);
            
            return (
              <div key={item.id} onClick={() => setSelectedItem(item)} className="bg-white rounded-[2.5rem] shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group border border-slate-100 overflow-hidden cursor-pointer relative">
                {user && user.uid === item.userId && (
                  <button 
                    onClick={(e) => handleDelete(e, item.id)}
                    className="absolute top-4 left-4 z-10 bg-white/80 hover:bg-red-100 text-red-500 p-2 rounded-full shadow-sm transition-colors border border-red-200"
                    title="삭제하기"
                  >
                    <Trash2 size={16} />
                  </button>
                )}

                <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
                  {item.imageUrl ? <><img src={item.imageUrl} alt={item.topic} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/><div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div></> : <div className="w-full h-full flex items-center justify-center text-slate-300"><Layout size={48}/></div>}
                  <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-md text-slate-800 text-xs font-bold px-4 py-2 rounded-full shadow-lg border border-white/50 font-['Noto_Sans_KR']">{item.grade} | {item.subject}</div>
                </div>
                  
                <div className="p-8">
                  <h3 className="font-bold text-xl text-slate-900 mb-3 line-clamp-1 group-hover:text-indigo-600 transition-colors font-['Noto_Sans_KR']">{item.topic || "제목 없음"}</h3>
                  <div className="flex flex-wrap gap-2 mb-6 min-h-[32px]">
                    {item.tech && item.tech.split(',').filter((t: string) => t.trim().length > 0).slice(0, 5).map((t: string, i: number) => (
                      <span key={i} className="text-[11px] font-extrabold bg-slate-50 text-slate-600 px-3 py-1.5 rounded-lg border border-slate-200 font-['Noto_Sans_KR']">
                        #{t.trim()}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-5 border-t border-slate-100">
                    <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center text-indigo-600 font-black text-sm border-2 border-white shadow-sm font-['Noto_Sans_KR']">{(item.userName || "T").charAt(0)}</div><span className="text-sm font-bold text-slate-700 font-['Noto_Sans_KR']">{item.userName || "익명"}</span></div>
                    <div className="flex gap-2">
                      <button className="flex items-center gap-1.5 text-slate-400 bg-slate-50 px-3 py-1.5 rounded-full"><MessageSquare size={16}/><span className="text-sm font-bold">{item.commentCount || 0}</span></button>
                      <button 
                        onClick={(e) => handleLike(e, item)} 
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all cursor-pointer ${isLiked ? 'bg-pink-100 text-pink-600 ring-2 ring-pink-200' : 'bg-pink-50 text-pink-400 hover:bg-pink-100'}`}
                      >
                        <Heart size={16} className={isLiked ? "fill-current" : ""} />
                        <span className="text-sm font-bold">{item.likes || 0}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
        
      {selectedItem && <BlueprintDetailModal blueprint={selectedItem} onClose={() => setSelectedItem(null)} user={user} />}
    </div>
  );
}
